import { useEffect, useMemo, useRef, useState } from 'react'

import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { BidSegment } from '@ebalance/proto/bfftypes/v1/bid_segment_pb'
import { LicenseType } from '@ebalance/proto/bfftypes/v1/license_type_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import { BiddingApprovalMode } from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

import type { HTMLChakraProps } from '@enechain/ecloud-designsystem'
import {
  Alert,
  Box,
  FieldRoot,
  Grid,
  HStack,
  Input,
  NativeSelectField,
  NativeSelectIndicator,
  NativeSelectRoot,
  PrimaryButton,
  RadioGroupItemControl,
  RadioGroupItemHiddenInput,
  RadioGroupRoot,
  FieldErrorText as RawFieldErrorText,
  FieldLabel as RawFieldLabel,
  RadioGroupItem as RawRadioGroupItem,
  RadioGroupItemText as RawRadioGroupItemText,
  TableBody,
  TableCell,
  TableColumnHeader,
  TableHeader,
  TableRoot,
  TableRow,
  TertiaryButton,
  Text,
  VStack,
} from '@enechain/ecloud-designsystem'

import {
  RegisteredIssuerSelector,
  useRegisteredIssuerOptions,
} from '~/components/bidding/common/IssuerSelector'
import {
  findIssuerOption,
  isAreaSelectableForIssuer,
  issuerAreaSelectOptions,
} from '~/components/bidding/common/bidIssuer'
import { initialBidArea } from '~/components/bidding/common/lastBidArea'
import { Badge } from '~/components/common/Badge'
import { Modal } from '~/components/common/Modal'
import { TradeSideLabel } from '~/components/common/TradeSideLabel'
import { useBidLimits } from '~/hooks/bidding/useBidLimits'
import { useListTradableAreas } from '~/hooks/bidding/useListTradableAreas'
import { useBusinessAreaFilter } from '~/hooks/useBusinessAreaFilter'
import { JEPX_AREA_CODES, convertAreaLabel } from '~/models/area'
import { checkBidLimit, resolveAreaSideLimit } from '~/models/bidLimits'
import { toProtoDate } from '~/models/dateTime'
import {
  KOMA_DIVIDER_ROW_CSS,
  isKomaDividerBetween,
} from '~/models/komaDivider'
import { formatKomaCompactLabel } from '~/models/komaLabel'
import { komaNumberToTime } from '~/models/komaToTime'
import {
  JEPX_PRICE_CONSTRAINT,
  JEPX_VOLUME_CONSTRAINT,
  constrainNumberInput,
  isPlainDecimalInput,
  violatesJepxStep,
} from '~/models/numberInputConstraint'
import { IntradayAreaEmptyState } from '~/pages/IntradayBidding/components/IntradayAreaEmptyState'
import { IntradayFormulaInputs } from '~/pages/IntradayBidding/components/IntradayFormulaFields'
import { IntradayPreparingRadioItem } from '~/pages/IntradayBidding/components/IntradayPreparingOption'
import {
  type IntradayPriceType,
  IntradayPriceTypeRadio,
} from '~/pages/IntradayBidding/components/IntradayPriceTypeRadio'
import { useIntradayBulkFormulaPreviews } from '~/pages/IntradayBidding/hooks/useIntradayBulkFormulaPreviews'
import { useIntradayFeeAdjustedPricePreview } from '~/pages/IntradayBidding/hooks/useIntradayFeeAdjustedPricePreview'
import type { IntradayFeeStateHandle } from '~/pages/IntradayBidding/hooks/useIntradayFeeState'
import { useIntradayFeeTreatment } from '~/pages/IntradayBidding/hooks/useIntradayFeeTreatment'
import { useIntradayFormulaFieldChoices } from '~/pages/IntradayBidding/hooks/useIntradayFormulaFields'
import { resolveIntradayAreaEmptyState } from '~/pages/IntradayBidding/models/areaEmptyState'
import type {
  IntradayBulkCreateConfirmedParams,
  IntradayBulkCreateOutcome,
  IntradayBulkCreateParams,
  IntradayBulkCreatePrice,
  IntradayBulkExistingBid,
  IntradayBulkFormulaRowPreview,
  IntradayBulkFormulaRowPrice,
  IntradayBulkPreviewRow,
} from '~/pages/IntradayBidding/models/bulkCreate'
import {
  buildIntradayBulkPreview,
  intradayBulkDuplicateTimeCodes,
  intradayBulkFormulaRowPrice,
  intradayBulkTargets,
  summarizeIntradayBulkFormulaPreviews,
} from '~/pages/IntradayBidding/models/bulkCreate'
import {
  fallbackOffPreview,
  feeAdjustmentViolationMessage,
  feeAutoConsiderHint,
  opensAsFeeFallbackOff,
  toFeeAutoConsiderMatrixSetting,
  usesDesiredPrice,
} from '~/pages/IntradayBidding/models/feeAutoConsider'
import {
  EMPTY_FORMULA_FIELD_VALUES,
  type IntradayFormulaFieldValues,
  formulaInputSummary,
  parseIntradayFormulaFormValues,
} from '~/pages/IntradayBidding/models/intradayFormula'
import { INTRADAY_P8_AVAILABLE } from '~/pages/IntradayBidding/models/intradayP8Availability'
import { intradayFormulaOffsetViolation } from '~/pages/IntradayBidding/models/schema'

/**
 * chakra v3 の `FieldLabelProps` / `FieldErrorTextProps` / `RadioGroupItem*Props` は
 * 本プロジェクトの型解決 (ecloud 0.14 / chakra 3.29 の d.ts 解決問題) で
 * `HTMLChakraProps` が collapse し `children` / 固有 prop が欠落する。 v3 本来の prop
 * シグネチャへ cast して復元する (= IntradayBidCreateModal と同じ回避)。
 */
const FieldLabelBase = RawFieldLabel as React.FC<HTMLChakraProps<'label'>>
const FieldLabel = (props: HTMLChakraProps<'label'>): React.ReactElement => (
  <FieldLabelBase
    textStyle="label"
    marginBottom={1}
    color="Text.highEmphasis"
    {...props}
  />
)
const FieldErrorText = RawFieldErrorText as React.FC<HTMLChakraProps<'div'>>
const RadioGroupItem = RawRadioGroupItem as React.FC<
  HTMLChakraProps<'div'> & { value: string }
>
const RadioGroupItemText = RawRadioGroupItemText as React.FC<
  HTMLChakraProps<'span'>
>

/** 必須フィールドの赤い `*` (= design `.form-label.required` の擬似要素相当)。 */
const RequiredMark = (): React.ReactElement => (
  <Text as="span" marginLeft={1} color="Text.negativeMid" fontWeight="bold">
    *
  </Text>
)

const KOMA_OPTIONS = Array.from({ length: 48 }, (_, i) => i + 1)

/** フォーミュラの 1 コマの価格列の文言 (中身は `intradayBulkFormulaRowPrice` が決める)。 */
const formulaRowPriceText = (
  t: TFunction,
  price: IntradayBulkFormulaRowPrice,
): string => {
  switch (price.kind) {
    case 'none':
      return '—'
    case 'unavailable':
      return t('page:biddingIntraday.bulkCreate.formulaPriceUnavailable')
    case 'notCalculable':
      return t('page:biddingIntraday.bulkCreate.formulaPriceNotCalculable')
    case 'price':
      return price.orderPriceYenPerKwh
  }
}

const FormulaRowPriceCell = ({
  preview,
}: {
  preview: IntradayBulkFormulaRowPreview | undefined
}): React.ReactElement => {
  const { t } = useTranslation()
  const price = intradayBulkFormulaRowPrice(preview)
  return (
    <Text
      as="span"
      color={
        price.kind === 'price' && price.isOutOfRange
          ? 'Text.negativeMid'
          : undefined
      }
    >
      {formulaRowPriceText(t, price)}
    </Text>
  )
}

type Props = {
  isOpen: boolean
  /** 画面が開く前から取得している手数料自動考慮の状態 (開いた時に読む、 AC-BIN-004b)。 */
  feeState: IntradayFeeStateHandle
  onClose: () => void
  /**
   * 入札追加タブの受渡日。 発注主体区分の選択肢とエリアの選択可否をこの日の広域マスタで
   * 求める (保存時の受渡契約コード解決と同じ断面。 #8842)。
   */
  deliveryDate: Date
  /** 全体フィルターのエリアが単一選択のときその値 (AC-BIN-122)。 */
  defaultAreaCode?: AreaCode
  /** 重複の警告に使う既存札 (受渡日の自社の札のうち、 承認待ち〜入札中の札だけを渡す)。 */
  existingBids: readonly IntradayBulkExistingBid[]
  /** GC 境界コマ (= このコマより前は GC 通過済として skip)。 */
  gcSlotIndex: number
  /**
   * フォームで選んだエリアの**実際の**承認モードを解決する (issue #8312 の PdM 判断、
   * 2026-09-17)。
   *
   * 本モーダルのエリア選択肢は画面上部のエリアフィルタに縛られず JEPX 全 9 エリアから
   * 選べる (= 選択肢は制限しないのが同判断) ため、 フィルタ基準の手順表示 (stepper) と
   * 実際の承認要否が食い違い得る。 手順表示側は現状維持と決まったので、 ここで
   * **確定前に案内**して埋める (= `IntradayBidCreateModal` と同じ扱い)。
   *
   * 未指定 (= 承認フロー設定が取得中・取得失敗で承認モードを確定できない間) は案内を
   * 出さない。 その間は一括作成の導線自体が非活性になっている。
   */
  resolveApprovalMode?: (areaCode: AreaCode) => BiddingApprovalMode
  /**
   * 確定。 `'feePreviewStale'` (手数料自動考慮の設定・単価・判定がプレビュー時から変わって
   * 拒否された) を返したら Step1 に戻り、 取り直したプレビューを見せる (#9213)。
   */
  onSubmit: (
    params: IntradayBulkCreateConfirmedParams,
    timeCodes: number[],
    /** プレビューで GC 通過済として外したコマ数 (結果の知らせ方に使う)。 */
    gcExcludedCount: number,
  ) => void | Promise<IntradayBulkCreateOutcome>
  isSubmitting?: boolean
}

type StepIndicatorProps = {
  step: 1 | 2
}

/** design `#intraday-bulk-add-modal` の 2 段 wizard インジケータ (入力 → プレビュー)。 */
const StepIndicator = ({ step }: StepIndicatorProps): React.ReactElement => {
  const { t } = useTranslation()
  return (
    <HStack alignItems="center" gap={2} marginBottom={4}>
      {(
        [
          { num: 1, label: t('page:biddingIntraday.bulkCreate.stepInput') },
          { num: 2, label: t('page:biddingIntraday.bulkCreate.stepPreview') },
        ] as const
      ).map((s, i) => (
        <HStack key={s.num} alignItems="center" flex={i === 0 ? undefined : 1}>
          {i > 0 && (
            <Box flex={1} height="1px" backgroundColor="Border.lowEmphasis" />
          )}
          <HStack gap={1.5}>
            <Text
              color={step === s.num ? 'Text.highEmphasis' : 'Text.midEmphasis'}
              fontWeight="semibold"
            >
              {s.num}
            </Text>
            <Text
              color={step === s.num ? 'Text.highEmphasis' : 'Text.midEmphasis'}
              fontWeight={step === s.num ? 'semibold' : 'normal'}
            >
              {s.label}
            </Text>
          </HStack>
        </HStack>
      ))}
    </HStack>
  )
}

/**
 * 時間前市場 一括作成 modal (design `design/bidding/intraday.html`
 * `#intraday-bulk-add-modal`、 spec AC-BIN-006b / 006c)。
 *
 * コマ範囲 × エリア × 売/買 × 価格 (固定価格 / フォーミュラ) × コマあたり MW を入力し (step 1)、
 * GC 通過済 / 既存重複の skip 判定を含むプレビュー (step 2) を確認してから確定する。
 * フォーミュラは変数の値がコマごとに違うので、 step 2 の表にコマごとの計算結果を出す (AC-BIN-005c)。
 * 確定後の永続化 (Create RPC のループ) は親 (= BidCreateTab) の責務。
 */
export const IntradayBulkCreateModal = ({
  isOpen,
  feeState,
  onClose,
  deliveryDate,
  defaultAreaCode,
  existingBids,
  gcSlotIndex,
  resolveApprovalMode,
  onSubmit,
  isSubmitting = false,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const [step, setStep] = useState<1 | 2>(1)
  const [slotFrom, setSlotFrom] = useState(21)
  const [slotTo, setSlotTo] = useState(28)
  // 発注主体区分を切り替えて選べなくなったら未選択 (undefined) に戻す (AC-BIN-004 ※)。
  const [areaCode, setAreaCode] = useState<AreaCode | undefined>(
    defaultAreaCode,
  )
  const [side, setSide] = useState('1')
  // 入札価格の入れ方 (AC-BIN-006b: 固定価格 / フォーミュラ)。
  const [priceType, setPriceType] = useState<IntradayPriceType>('fixed')
  const [price, setPrice] = useState('')
  const [formulaValues, setFormulaValues] =
    useState<IntradayFormulaFieldValues>(EMPTY_FORMULA_FIELD_VALUES)
  const [volumeMw, setVolumeMw] = useState('')
  // 発注主体区分。 生成札全件に同一適用する (issue #5366)。
  const [issuerType, setIssuerType] = useState<LicenseType>(
    LicenseType.UNSPECIFIED,
  )
  const issuerOptions = useRegisteredIssuerOptions(deliveryDate)
  const issuer = findIssuerOption(issuerOptions, issuerType)
  const { selectableAreaCodes, isBusinessAreasResolved } =
    useBusinessAreaFilter(JEPX_AREA_CODES)
  const { data: tradableAreasData } = useListTradableAreas(deliveryDate)
  const issuerRef = useRef(issuer)
  issuerRef.current = issuer
  const selectableAreaCodesRef = useRef(selectableAreaCodes)
  selectableAreaCodesRef.current = selectableAreaCodes
  const [errors, setErrors] = useState<Record<string, string>>({})

  // AC-BIN-100 (一括作成への適用は issue #8307 の PdM 判断): 上限数量(1札) の
  // 実設定値 (settings「入札制限」INTRADAY segment)。一括作成は生成する全札に
  // 同一の エリア × 売買 × コマあたり MW を適用するため、判定はフォーム全体で
  // 1 回でよい (行ごとの判定は不要)。
  // 取得中 (isLoading) は lookup が空のまま (= 制限なし相当) になり、その間に
  // プレビューへ進むと上限超過でもチェックを素通りしてしまう。取得中は「次へ」を
  // disabled にしてこの窓を塞ぐ (= IntradayBidCreateModal と同じ扱い)。取得失敗は
  // 意図的な fail-safe (= 制限は入力補助でしか
  // ないため画面を止めない) なので disabled にしない。
  const {
    limits: bidLimits,
    isLoading: isBidLimitsLoading,
    isFetching: isBidLimitsFetching,
    isError: isBidLimitsError,
    feeAutoConsiderEnabled,
  } = useBidLimits(BidSegment.INTRADAY)
  // 確定が手数料自動考慮の変化で拒否されて Step1 に戻った後は、 取り直したプレビューの違反を
  // 「プレビュー」 を押す前から価格欄に出す。
  const [feeRejected, setFeeRejected] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    setStep(1)
    setSlotFrom(21)
    setSlotTo(28)
    setAreaCode(
      initialBidArea(
        'intraday',
        defaultAreaCode,
        (code) =>
          isAreaSelectableForIssuer(issuerRef.current, code) &&
          selectableAreaCodesRef.current.includes(code),
      ),
    )
    setSide('1')
    setPriceType('fixed')
    setPrice('')
    setFormulaValues(EMPTY_FORMULA_FIELD_VALUES)
    setVolumeMw('')
    setErrors({})
    setFeeRejected(false)
  }, [isOpen, defaultAreaCode])

  // 売買は radio の文字列値 ('1' = 買 / '2' = 売) で持つ。 プレビュー表示・生成
  // パラメータ・上限数量(1札) の lookup が同じ値を見るよう 1 箇所で enum 化する。
  const sideEnum = side === '2' ? Side.SELL : Side.BUY
  // AC-BIN-004b / AC-BIN-104: 入札価格と値域・入札制限の判定は backend のプレビュー RPC
  // の結果を出す (#9213)。 全札が同じエリア・売買・価格なので 1 行で問い合わせる。
  // エリアは発注主体の切替で未選択に戻ることがある (AC-BIN-004 ※) ので、 個別作成と同じく
  // エリアと価格が揃った時だけ問い合わせる。
  const feeMatrixSetting = toFeeAutoConsiderMatrixSetting({
    // 確定が拒否された後の取り直し中は、 取り直す前の ON/OFF とプレビューを突き合わせない。
    isLoading: isBidLimitsFetching,
    isError: isBidLimitsError,
    enabled: feeAutoConsiderEnabled,
  })
  const isFormula = priceType === 'formula'
  // AC-BIN-004b: 手数料単価を取得できない間は OFF として扱い、 固定価格は入力値を、 フォーミュラは
  // コマごとの計算結果をそのまま入札価格にする。
  const fee = useIntradayFeeTreatment({
    isOpen,
    opensAsFallbackOff: opensAsFeeFallbackOff(feeState.state, feeMatrixSetting),
    setting: feeMatrixSetting,
    feeState,
  })
  const isFeeFallbackOff = fee.treatment.status === 'fallbackOff'
  // フォーミュラの計算結果はプレビューの段でコマごとに出すので、 入力欄の近くには OFF として扱う旨だけ出す。
  const formulaFallbackOffHint =
    fee.treatment.status === 'fallbackOff'
      ? feeAutoConsiderHint(fallbackOffPreview(fee.treatment.setting))
      : undefined
  const feePreview = useIntradayFeeAdjustedPricePreview(
    !isFormula && areaCode !== undefined && isPlainDecimalInput(price)
      ? { areaCode, direction: sideEnum, desiredPriceYenPerKwh: price }
      : undefined,
    fee,
  )
  const feeHint = feeAutoConsiderHint(feePreview)
  const priceError =
    errors.price ??
    (feeRejected && feePreview.kind === 'outOfRange'
      ? feeAdjustmentViolationMessage(t, feePreview.violation)
      : undefined)

  // AC-BIN-005: フォーミュラ欄。 参照エリアは選ぶまで一括作成で選んだエリアに追従する。
  const areaCodeValue = areaCode === undefined ? '' : String(areaCode)
  const formulaChoices = useIntradayFormulaFieldChoices({
    isOpen: isOpen && isFormula,
    deliveryDate,
    values: { areaCode: areaCodeValue, ...formulaValues },
  })
  const formulaInput = isFormula
    ? parseIntradayFormulaFormValues({
        areaCode: areaCodeValue,
        ...formulaValues,
      })
    : undefined

  const params = useMemo<IntradayBulkCreateParams>(
    () => ({
      slotFrom,
      slotTo,
      // 未選択のうちは validate が先へ進ませない (= プレビュー・確定は必ず選択済み)。
      areaCode: areaCode ?? AreaCode.UNSPECIFIED,
      direction: sideEnum,
      volumeMw,
      // 発注主体区分は生成札全件へ同一適用する。 未選択のうちは確定させない。
      issuerType: issuer?.issuerType ?? LicenseType.UNSPECIFIED,
    }),
    [slotFrom, slotTo, areaCode, sideEnum, volumeMw, issuer],
  )

  const rows = useMemo<IntradayBulkPreviewRow[]>(
    () => (step === 2 ? buildIntradayBulkPreview(params, gcSlotIndex) : []),
    [step, params, gcSlotIndex],
  )
  const targets = useMemo(() => intradayBulkTargets(rows), [rows])
  const skipCount = rows.length - targets.length

  // AC-BIN-005c: フォーミュラはコマごとに変数の値が違うので、 作成対象のコマごとに計算結果を出す。
  const deliveryProtoDate = useMemo(
    () => toProtoDate(deliveryDate),
    [deliveryDate],
  )
  const formulaRowPreviews = useIntradayBulkFormulaPreviews({
    targetDate: deliveryProtoDate,
    timeCodes: targets,
    areaCode,
    direction: sideEnum,
    formula: formulaInput,
    fee,
  })
  // 札作成モーダルと同じく、 計算できた行の手数料の確認が要る間・入札価格が範囲外の行がある間は確定しない。
  const formulaSummary = summarizeIntradayBulkFormulaPreviews(
    [...formulaRowPreviews.values()],
    fee.treatment,
  )
  // 範囲外の文言は「直し方\n理由」 の 2 行。 1 行目を Alert の見出し、 2 行目を本文にする。
  const outOfRangeMessage =
    isFormula && formulaSummary.outOfRangeViolation !== undefined
      ? feeAdjustmentViolationMessage(t, formulaSummary.outOfRangeViolation)
      : undefined
  const [outOfRangeTitle, outOfRangeReason] =
    outOfRangeMessage?.split('\n') ?? []
  // 重複の警告 (AC-BIN-006b) の入札価格は、 手数料自動考慮が ON なら加減算後の実際に送る価格。
  const fixedOrderPrice =
    feePreview.kind === 'ready' && feePreview.orderPriceYenPerKwh !== undefined
      ? feePreview.orderPriceYenPerKwh
      : price
  const duplicateCount = useMemo(
    () =>
      intradayBulkDuplicateTimeCodes(
        params,
        existingBids,
        targets,
        (timeCode) => {
          if (!isFormula)
            return isPlainDecimalInput(price) ? fixedOrderPrice : undefined
          const rowPrice = intradayBulkFormulaRowPrice(
            formulaRowPreviews.get(timeCode),
          )
          return rowPrice.kind === 'price'
            ? rowPrice.orderPriceYenPerKwh
            : undefined
        },
      ).length,
    [
      params,
      existingBids,
      targets,
      isFormula,
      price,
      fixedOrderPrice,
      formulaRowPreviews,
    ],
  )
  const priceSummaryText = isFormula
    ? formulaInput === undefined
      ? '—'
      : formulaInputSummary(t, formulaInput)
    : t('page:biddingIntraday.bulkCreate.summaryPriceValue', {
        price: Number(price).toFixed(2),
      })

  const validate = (): boolean => {
    const next: Record<string, string> = {}
    if (areaCode === undefined) {
      next.areaCode = t('page:biddingIntraday.bulkCreate.errorAreaRequired')
    }
    if (slotFrom > slotTo) {
      next.slotTo = t('page:biddingIntraday.bulkCreate.errorSlotOrder')
    }
    // ON の範囲は希望価格ではなく算出した入札価格で決まる (AC-BIN-104)。 0 以下の希望価格も
    // プレビューの結果に従い、 「0 より大きい」 の検証は OFF (入力値 = 入札価格) にだけ掛ける。
    const isAdjustedByFee =
      feePreview.kind === 'ready' && feePreview.confirmed.enabled
    if (isFormula) {
      // AC-BIN-052: フォーミュラ選択時はオフセット値だけを見る (計算結果の値域は backend が判定する)。
      const offsetMessage = intradayFormulaOffsetViolation(
        formulaValues.formulaOffset,
      )
      if (offsetMessage !== undefined) next.formulaOffset = offsetMessage
    } else if (!isPlainDecimalInput(price)) {
      next.price = t('page:biddingIntraday.bulkCreate.errorPositive')
    } else if (feePreview.kind === 'outOfRange') {
      next.price = feeAdjustmentViolationMessage(t, feePreview.violation)
    } else if (!isAdjustedByFee && Number(price) <= 0) {
      next.price = t('page:biddingIntraday.bulkCreate.errorPositive')
    }
    const volumeNum = Number(volumeMw)
    if (volumeMw === '' || !Number.isFinite(volumeNum) || volumeNum <= 0) {
      next.volumeMw = t('page:biddingIntraday.bulkCreate.errorPositive')
    } else if (violatesJepxStep(volumeNum, JEPX_VOLUME_CONSTRAINT.step)) {
      // 書込 contract の 0.1 MWh グリッドに載らない値は送信時に丸められるため入口で弾く。
      next.volumeMw = t('page:biddingIntraday.bulkCreate.errorVolumeStep', {
        step: JEPX_VOLUME_CONSTRAINT.step,
      })
    } else if (areaCode !== undefined) {
      // AC-BIN-100 (issue #8307): コマあたり MW が上限数量(1札) を超える場合は
      // プレビューへ進ませない。 生成される札はすべて同一数量なので、 超過なら
      // 全札が backend のゲートで弾かれる。 文言は個別作成モーダルと同じものを
      // 使う (AC-BIN-052: 制限値と入力値を併記)。 クランプは行わない
      // (bid-limits.md §2-2)。
      const limit = resolveAreaSideLimit(bidLimits, areaCode, sideEnum)
      if (checkBidLimit(undefined, volumeNum, limit).includes('volume-max')) {
        next.volumeMw = t(
          'page:biddingIntraday.form.volumeExceedsPerBidLimit',
          { max: limit?.qtyMax, value: volumeNum },
        )
      }
    }
    setErrors(next)
    return Object.keys(next).length === 0
  }

  // 「プレビュー」 は blocked の間押せず、 値域外・入力不足は validate() がエラーを出す。
  const canGoToPreview =
    issuer !== undefined && !isBidLimitsLoading && feePreview.kind !== 'blocked'
  const areaOptions = issuerAreaSelectOptions(
    issuer,
    JEPX_AREA_CODES,
    areaCode,
    (area) => !selectableAreaCodes.includes(area),
    convertAreaLabel,
  )
  const areaEmptyState = resolveIntradayAreaEmptyState({
    issuer,
    businessAreaCodes: isBusinessAreasResolved
      ? selectableAreaCodes
      : undefined,
    registrationIssues: tradableAreasData?.registrationIssues,
    jepxAreaCodes: JEPX_AREA_CODES,
  })
  useEffect(() => {
    if (areaCode !== undefined && !selectableAreaCodes.includes(areaCode)) {
      setAreaCode(undefined)
    }
  }, [areaCode, selectableAreaCodes])
  const handleNext = (): void => {
    setFeeRejected(false)
    if (!validate() || !canGoToPreview) return
    setStep(2)
  }

  // AC-BIN-006c: 追加対象 0 件は確定させない。 手数料自動考慮はプレビューが作成してよい
  // 状態の時だけ確定できる (Step2 を開いたまま取り直した場合を含む)。
  const confirmedPrice = ((): IntradayBulkCreatePrice | undefined => {
    if (isFormula) {
      return formulaInput !== undefined && !formulaSummary.isBlocking
        ? {
            price: {
              kind: 'formula',
              formula: formulaInput,
              treatFeeAutoConsiderAsOff: isFeeFallbackOff,
            },
          }
        : undefined
    }
    switch (feePreview.kind) {
      case 'fallbackOff':
        return { price: { kind: 'orderPrice', orderPriceYenPerKwh: price } }
      case 'ready':
        return {
          price: {
            kind: 'limit',
            desiredPriceYenPerKwh: price,
            feeAutoConsider: feePreview.confirmed,
          },
        }
      default:
        return undefined
    }
  })()
  // Step2 を開いた後にも業務エリア設定・広域機関の登録は変わり得る。選択を外す effect と
  // 同じ現在値を確定条件でも見ることで、古い preview から未選択エリアの札を送らない。
  const isCurrentAreaSelectable =
    areaCode !== undefined &&
    selectableAreaCodes.includes(areaCode) &&
    isAreaSelectableForIssuer(issuer, areaCode)
  const canConfirm =
    targets.length > 0 &&
    confirmedPrice !== undefined &&
    isCurrentAreaSelectable &&
    areaEmptyState === undefined
  const handleConfirm = (): void => {
    if (!canConfirm) return
    void Promise.resolve(
      onSubmit({ ...params, ...confirmedPrice }, targets, skipCount),
    ).then((outcome) => {
      if (outcome === 'feePreviewStale') {
        setErrors({})
        setFeeRejected(true)
        setStep(1)
      }
    })
  }

  // 選んだエリアの実際の承認モード (issue #8312、 [[Props.resolveApprovalMode]] 参照)。
  // 生成する全札に同一適用されるエリアなので、 入力 step のエリア欄の直下で案内する。
  const areaApprovalMode =
    areaCode !== undefined ? resolveApprovalMode?.(areaCode) : undefined

  return (
    <Modal
      footer={
        <>
          <TertiaryButton marginRight={3} onClick={onClose}>
            {t('common:action.cancel')}
          </TertiaryButton>
          {step === 2 && (
            <TertiaryButton marginRight={3} onClick={() => setStep(1)}>
              {t('page:biddingIntraday.bulkCreate.back')}
            </TertiaryButton>
          )}
          {step === 1 ? (
            <PrimaryButton
              disabled={!canGoToPreview || areaEmptyState !== undefined}
              onClick={handleNext}
            >
              {t('page:biddingIntraday.bulkCreate.toPreview')}
            </PrimaryButton>
          ) : (
            <PrimaryButton
              disabled={!canConfirm}
              loading={isSubmitting}
              onClick={handleConfirm}
            >
              {t('page:biddingIntraday.bulkCreate.confirm')}
            </PrimaryButton>
          )}
        </>
      }
      isOpen={isOpen}
      onClose={onClose}
      scrollBehavior="inside"
      size="lg"
      title={t('page:biddingIntraday.bulkCreate.title')}
    >
      <Box>
        <StepIndicator step={step} />

        {step === 1 && areaEmptyState !== undefined ? (
          <VStack align="stretch" gap={3}>
            <RegisteredIssuerSelector
              deliveryDate={deliveryDate}
              onChange={(next, option) => {
                setIssuerType(next)
                setAreaCode((prev) =>
                  prev !== undefined &&
                  option !== undefined &&
                  (!isAreaSelectableForIssuer(option, prev) ||
                    !selectableAreaCodes.includes(prev))
                    ? undefined
                    : prev,
                )
              }}
              selectsDefault
              value={issuerType}
            />
            <IntradayAreaEmptyState state={areaEmptyState} />
          </VStack>
        ) : step === 1 ? (
          <VStack align="stretch" gap={3}>
            {/* 発注主体区分。 生成される全札に同一適用する。
                以前はこの欄が無く、 時間前の札はどの名義で出したか残らなかった
                (issue #5366)。 */}
            <RegisteredIssuerSelector
              deliveryDate={deliveryDate}
              onChange={(next, option) => {
                setIssuerType(next)
                // 選択中のエリアが新しい区分で選べなければ選択解除する
                // (AC-BIN-004 ※。 スポット AC-BSP-005b と同じ)。
                setAreaCode((prev) =>
                  prev !== undefined &&
                  option !== undefined &&
                  (!isAreaSelectableForIssuer(option, prev) ||
                    !selectableAreaCodes.includes(prev))
                    ? undefined
                    : prev,
                )
              }}
              selectsDefault
              value={issuerType}
            />
            <Grid gridGap={3} gridTemplateColumns="1fr 1fr">
              <FieldRoot required>
                <FieldLabel htmlFor="intradayBulk-slotFrom">
                  {t('page:biddingIntraday.bulkCreate.fieldSlotFrom')}
                  <RequiredMark />
                </FieldLabel>
                <NativeSelectRoot>
                  <NativeSelectField
                    fontSize="md"
                    id="intradayBulk-slotFrom"
                    onChange={(e) => setSlotFrom(Number(e.target.value))}
                    value={slotFrom}
                  >
                    {KOMA_OPTIONS.map((k) => (
                      <option key={k} value={k}>
                        {formatKomaCompactLabel(t, k)}
                      </option>
                    ))}
                  </NativeSelectField>
                  <NativeSelectIndicator />
                </NativeSelectRoot>
              </FieldRoot>
              <FieldRoot invalid={errors.slotTo !== undefined} required>
                <FieldLabel htmlFor="intradayBulk-slotTo">
                  {t('page:biddingIntraday.bulkCreate.fieldSlotTo')}
                  <RequiredMark />
                </FieldLabel>
                <NativeSelectRoot>
                  <NativeSelectField
                    fontSize="md"
                    id="intradayBulk-slotTo"
                    onChange={(e) => setSlotTo(Number(e.target.value))}
                    value={slotTo}
                  >
                    {KOMA_OPTIONS.map((k) => (
                      <option key={k} value={k}>
                        {formatKomaCompactLabel(t, k)}
                      </option>
                    ))}
                  </NativeSelectField>
                  <NativeSelectIndicator />
                </NativeSelectRoot>
                <FieldErrorText>{errors.slotTo}</FieldErrorText>
              </FieldRoot>
            </Grid>

            <FieldRoot invalid={errors.areaCode !== undefined} required>
              <FieldLabel htmlFor="intradayBulk-area">
                {t('page:biddingIntraday.bulkCreate.fieldArea')}
                <RequiredMark />
              </FieldLabel>
              <NativeSelectRoot>
                <NativeSelectField
                  fontSize="md"
                  id="intradayBulk-area"
                  onChange={(e) =>
                    setAreaCode(
                      e.target.value === ''
                        ? undefined
                        : (Number(e.target.value) as AreaCode),
                    )
                  }
                  value={areaCode ?? ''}
                >
                  <option value="">{t('common:form.selectPlaceholder')}</option>
                  {areaOptions.map(({ area, label, disabled }) => (
                    <option key={area} disabled={disabled} value={area}>
                      {label}
                    </option>
                  ))}
                </NativeSelectField>
                <NativeSelectIndicator />
              </NativeSelectRoot>
              <FieldErrorText>{errors.areaCode}</FieldErrorText>
              {/* 選んだエリアの承認要否の案内 (issue #8312 PdM 判断)。 手順表示 (stepper) は
                  画面上部のエリアフィルタ基準のままなので、 フィルタ外のエリアを選んだ時に
                  実際の承認要否をここで示す (= 個別作成モーダルと同じ文言・スタイル)。 */}
              {areaApprovalMode !== undefined && (
                <Text marginTop={1} color="Text.midEmphasis" fontSize="sm">
                  {t(
                    areaApprovalMode === BiddingApprovalMode.APPROVAL
                      ? 'page:biddingIntraday.form.approvalModeRequired'
                      : 'page:biddingIntraday.form.approvalModeNotRequired',
                  )}
                </Text>
              )}
            </FieldRoot>

            <FieldRoot required>
              <FieldLabel>
                {t('page:biddingIntraday.bulkCreate.fieldSide')}
                <RequiredMark />
              </FieldLabel>
              <RadioGroupRoot
                onValueChange={(details: { value: string | null }) =>
                  setSide(details.value ?? '1')
                }
                value={side}
              >
                <HStack gap={4}>
                  <RadioGroupItem value="1">
                    <RadioGroupItemHiddenInput />
                    <RadioGroupItemControl />
                    <RadioGroupItemText>
                      <TradeSideLabel side={Side.BUY} />
                    </RadioGroupItemText>
                  </RadioGroupItem>
                  <RadioGroupItem value="2">
                    <RadioGroupItemHiddenInput />
                    <RadioGroupItemControl />
                    <RadioGroupItemText>
                      <TradeSideLabel side={Side.SELL} />
                    </RadioGroupItemText>
                  </RadioGroupItem>
                </HStack>
              </RadioGroupRoot>
            </FieldRoot>

            {/* AC-BIN-006b: 入札方式は 通常 / 戦略。 戦略は P8 の機能で、 選択肢に出したまま非活性に
                し、 理由は名前側のツールチップで示す (AC-BIN-133)。 一括作成にアイスバーグは無い。
                この項目は準備中を示すためだけにあり、 `INTRADAY_P8_AVAILABLE` が true の間は出さない
                (一括作成は戦略の札を作れない)。 */}
            {!INTRADAY_P8_AVAILABLE && (
              <FieldRoot>
                <FieldLabel>
                  {t('page:biddingIntraday.form.bidMode')}
                </FieldLabel>
                <RadioGroupRoot value="normal">
                  <HStack gap={4}>
                    <RadioGroupItem value="normal">
                      <RadioGroupItemHiddenInput />
                      <RadioGroupItemControl />
                      <RadioGroupItemText>
                        {t('page:biddingIntraday.form.bidModeNormal')}
                      </RadioGroupItemText>
                    </RadioGroupItem>
                    <IntradayPreparingRadioItem
                      label={t('page:biddingIntraday.form.bidModeStrategy')}
                      value="strategy"
                    />
                  </HStack>
                </RadioGroupRoot>
              </FieldRoot>
            )}

            <Grid gridGap={3} gridTemplateColumns="1fr 1fr">
              <FieldRoot
                // フォーミュラ欄は 2 行に並ぶ幅が要るので、 横 1 列を使う。
                gridColumn={isFormula ? '1 / -1' : undefined}
                invalid={
                  isFormula
                    ? errors.formulaOffset !== undefined
                    : priceError !== undefined
                }
                required
              >
                <FieldLabel htmlFor="intradayBulk-price">
                  {t(
                    usesDesiredPrice(fee.treatment, feeAutoConsiderEnabled)
                      ? 'page:biddingIntraday.bulkCreate.fieldDesiredPrice'
                      : 'page:biddingIntraday.bulkCreate.fieldPrice',
                  )}
                  <RequiredMark />
                </FieldLabel>
                {/* AC-BIN-006b: 入札価格は 固定価格 / フォーミュラ。 */}
                <Box marginBottom={2}>
                  <IntradayPriceTypeRadio
                    onChange={(next) => {
                      setPriceType(next)
                      // 切り替える前の価格欄のエラーを残さない。
                      setErrors((prev) => {
                        const rest = { ...prev }
                        delete rest.price
                        delete rest.formulaOffset
                        return rest
                      })
                    }}
                    value={priceType}
                  />
                </Box>
                {!isFormula ? (
                  <>
                    <Input
                      fontSize="md"
                      textAlign="right"
                      id="intradayBulk-price"
                      max={JEPX_PRICE_CONSTRAINT.max}
                      min={JEPX_PRICE_CONSTRAINT.min}
                      onChange={(e) =>
                        setPrice(
                          constrainNumberInput(
                            e.target.value,
                            JEPX_PRICE_CONSTRAINT,
                          ),
                        )
                      }
                      placeholder="11.50"
                      step={JEPX_PRICE_CONSTRAINT.step}
                      type="number"
                      value={price}
                    />
                    {/* AC-BIN-004b: 入札価格の補足表示 (design intraday.html #ibulk-fee-preview)。 */}
                    <Text marginTop={1} color={feeHint.color} fontSize="sm">
                      {t(`page:biddingIntraday.bulkCreate.${feeHint.key}`, {
                        price: feeHint.orderPriceYenPerKwh,
                      })}
                    </Text>
                    <FieldErrorText whiteSpace="pre-line">
                      {priceError}
                    </FieldErrorText>
                  </>
                ) : (
                  <>
                    <IntradayFormulaInputs
                      fields={formulaChoices}
                      offsetError={errors.formulaOffset}
                      offsetInputId="intradayBulk-formulaOffset"
                      onChange={(name, value) =>
                        setFormulaValues((prev) => ({ ...prev, [name]: value }))
                      }
                      values={formulaValues}
                    />
                    {/* AC-BIN-005c: コマごとに変数の値が違うので、 計算結果はプレビューでコマごとに出す。 */}
                    <Text marginTop={1} color="Text.midEmphasis" fontSize="sm">
                      {t('page:biddingIntraday.bulkCreate.formulaPreviewNote')}
                    </Text>
                    {formulaFallbackOffHint !== undefined && (
                      <Text color={formulaFallbackOffHint.color} fontSize="sm">
                        {t(
                          `page:biddingIntraday.bulkCreate.${formulaFallbackOffHint.key}`,
                        )}
                      </Text>
                    )}
                  </>
                )}
              </FieldRoot>
              <FieldRoot invalid={errors.volumeMw !== undefined} required>
                <FieldLabel htmlFor="intradayBulk-volumeMw">
                  {t('page:biddingIntraday.bulkCreate.fieldVolumeMw')}
                  <RequiredMark />
                </FieldLabel>
                <Input
                  fontSize="md"
                  textAlign="right"
                  id="intradayBulk-volumeMw"
                  max={JEPX_VOLUME_CONSTRAINT.max}
                  min={JEPX_VOLUME_CONSTRAINT.min}
                  onChange={(e) =>
                    setVolumeMw(
                      constrainNumberInput(
                        e.target.value,
                        JEPX_VOLUME_CONSTRAINT,
                      ),
                    )
                  }
                  placeholder="50"
                  step={JEPX_VOLUME_CONSTRAINT.step}
                  type="number"
                  value={volumeMw}
                />
                <FieldErrorText>{errors.volumeMw}</FieldErrorText>
              </FieldRoot>
            </Grid>

            <Alert
              status="info"
              title={t('page:biddingIntraday.bulkCreate.skipNotice')}
            />
          </VStack>
        ) : (
          <VStack align="stretch" gap={3}>
            <Box>
              <HStack flexWrap="wrap" gap={2}>
                {areaCode !== undefined && (
                  <Text as="strong">{convertAreaLabel(areaCode)}</Text>
                )}
                <Text>
                  {t('page:biddingIntraday.bulkCreate.summaryAreaSuffix')}
                </Text>
                <TradeSideLabel side={sideEnum} />
                <Text>
                  {t('page:biddingIntraday.bulkCreate.summaryPriceLabel')}{' '}
                  <Text as="strong">{priceSummaryText}</Text>{' '}
                  {t('page:biddingIntraday.bulkCreate.summaryVolumeLabel')}{' '}
                  <Text as="strong">
                    {t('page:biddingIntraday.bulkCreate.summaryVolumeValue', {
                      volume: volumeMw,
                    })}
                  </Text>
                </Text>
              </HStack>
              <HStack gap={2}>
                <Text color="Text.positiveMid">
                  {t('page:biddingIntraday.bulkCreate.summaryTargetCount', {
                    count: targets.length,
                  })}
                </Text>
                <Text color="Text.midEmphasis">
                  {t('page:biddingIntraday.bulkCreate.summarySkipCount', {
                    count: skipCount,
                  })}
                </Text>
              </HStack>
            </Box>

            {duplicateCount > 0 && (
              <Alert
                description={t(
                  'page:biddingIntraday.bulkCreate.duplicateWarning.body',
                )}
                status="warning"
                title={t(
                  'page:biddingIntraday.bulkCreate.duplicateWarning.title',
                  { count: duplicateCount },
                )}
              />
            )}

            {targets.length === 0 && (
              <Alert
                status="error"
                title={t('page:biddingIntraday.bulkCreate.emptyTarget')}
              />
            )}

            {/* AC-BIN-004b: フォーミュラの価格列は、 計算結果に手数料を加減算した入札価格。 */}
            {isFormula && formulaSummary.feeHint !== undefined && (
              <Text color={formulaSummary.feeHint.color} fontSize="sm">
                {t(
                  `page:biddingIntraday.bulkCreate.${formulaSummary.feeHint.key}`,
                )}
              </Text>
            )}
            {/* AC-BIN-104: 入札価格が範囲外のコマがあれば、 最初のコマの違反を同じ文言で出して確定を止める。 */}
            {outOfRangeMessage !== undefined && (
              <Alert
                description={outOfRangeReason}
                status="error"
                title={outOfRangeTitle}
              />
            )}

            <Box overflowY="auto" maxHeight="320px">
              <TableRoot fontSize="md" size="sm" variant="line">
                <TableHeader
                  position="sticky"
                  zIndex={1}
                  top={0}
                  backgroundColor="Surface.primary"
                >
                  <TableRow>
                    <TableColumnHeader>
                      {t('page:biddingIntraday.bulkCreate.columnKoma')}
                    </TableColumnHeader>
                    <TableColumnHeader>
                      {t('page:biddingIntraday.bulkCreate.columnTime')}
                    </TableColumnHeader>
                    <TableColumnHeader textAlign="center">
                      {t('page:biddingIntraday.bulkCreate.columnSide')}
                    </TableColumnHeader>
                    <TableColumnHeader textAlign="end">
                      {t('page:biddingIntraday.bulkCreate.columnVolume')}
                    </TableColumnHeader>
                    <TableColumnHeader textAlign="end">
                      {t('page:biddingIntraday.bulkCreate.columnPrice')}
                    </TableColumnHeader>
                    <TableColumnHeader>
                      {t('page:biddingIntraday.bulkCreate.columnJudgement')}
                    </TableColumnHeader>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row, rowIndex) => (
                    <TableRow
                      key={row.timeCode}
                      // 3 時間 (6 コマ) ごとの区切り線 (conventions.md §48 コマの表の区切り線)
                      opacity={row.skipReason !== undefined ? 0.5 : 1}
                      css={
                        isKomaDividerBetween(
                          row.timeCode,
                          rows[rowIndex + 1]?.timeCode,
                        )
                          ? KOMA_DIVIDER_ROW_CSS
                          : undefined
                      }
                    >
                      <TableCell whiteSpace="nowrap">
                        {t('page:biddingIntraday.bulkCreate.komaCell', {
                          koma: row.timeCode,
                        })}
                      </TableCell>
                      <TableCell whiteSpace="nowrap">
                        {komaNumberToTime(row.timeCode)}
                      </TableCell>
                      <TableCell textAlign="center">
                        <TradeSideLabel side={sideEnum} />
                      </TableCell>
                      <TableCell
                        textAlign="end"
                        css={{ fontVariantNumeric: 'tabular-nums' }}
                      >
                        {volumeMw}
                      </TableCell>
                      <TableCell
                        textAlign="end"
                        css={{ fontVariantNumeric: 'tabular-nums' }}
                      >
                        {isFormula ? (
                          <FormulaRowPriceCell
                            preview={formulaRowPreviews.get(row.timeCode)}
                          />
                        ) : (
                          Number(price).toFixed(2)
                        )}
                      </TableCell>
                      <TableCell whiteSpace="nowrap">
                        {row.skipReason === undefined ? (
                          <Badge status="positive">
                            {t('page:biddingIntraday.bulkCreate.badgeAdd')}
                          </Badge>
                        ) : (
                          <Badge status="neutral">
                            {t(
                              'page:biddingIntraday.bulkCreate.badgeSkipGcPassed',
                            )}
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </TableRoot>
            </Box>
          </VStack>
        )}
      </Box>
    </Modal>
  )
}
